import { type ReactNode } from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import { CALL, options } from '@/karehero-app/handover/dynamic-flow-app';
import { chat, homeTab } from '@/karehero-app/handover/dynamic-flow-slices';
import { type ChatQuestion } from '@/karehero-app/tell-us-once/intake/content';
import { INTAKE_CHAT_V2_0 } from '@/karehero-app/tell-us-once/intake/content.v2-0';
import FromUsCard from '@/karehero-app/tell-us-once/intake/FromUsCard';
import HomeConcept2 from '@/karehero-app/tell-us-once/intake/HomeConcept2';

const noop = () => {};

// Onboarding's own turns: the opening question, answered in their words, and
// the summary card 4.0 reads back, kept as the alternative to compare against.
const HELP = INTAKE_CHAT_V2_0.find((q) => q.id === 'help')!;
const SUMMARY = INTAKE_CHAT_V2_0.find((q) => q.id === 'summary')!;

const summary = (items: string[]): ChatQuestion[] => [
  { ...SUMMARY, card: { ...SUMMARY.card!, items } },
];

// Said back in the conversation instead of the card: their words, then Keni
// acknowledging it, saying it back in a sentence or two, and checking it. Then
// what comes next, which depends on whether they said what they need.
//
// No reply chips: it is a conversation, so they answer in their own words.
// Answers are typed rather than said, so the words show beside what is said
// back.
const repeatBack = (p: Person, answered = false): ChatQuestion[] => [
  // No reading: the scene opens after it, so it would only hold the chat up.
  { ...HELP, reading: undefined, voice: false, answer: p.words },
  {
    id: 'repeat',
    says: [p.acknowledge, p.repeat],
    ask: 'Have I got that right?',
    mode: 'say',
    // The mic stays while it is being asked; once answered, typed.
    voice: !answered,
    answer: "Yes, that's right",
  },
  {
    id: 'then',
    says: p.then.says,
    ask: p.then.ask,
    mode: 'say',
  },
];

// The lead: one thing, its reason, and the button that starts it.
const lead = (label: string, body: string, action: string) => (
  <HomeConcept2
    todo
    conversations={false}
    todoList={{ label, body, items: [], action }}
  />
);

const toldUs = (items: string[]) => (
  <FromUsCard
    label={`What you've told us`}
    items={items}
    sentence
    onEdit={noop}
  />
);

// Something onboarding heard that we can't help with yet. Shown so the gap is
// visible on the page, not as a decision that Home would show it.
const gap = (label: string, body: string) => (
  <FromUsCard label={label} body={body} sentence>
    <Chip
      label='Not offered yet'
      size='small'
      sx={{ alignSelf: 'flex-start' }}
    />
  </FromUsCard>
);

const why = (text: string) => (
  <Note title='Why this leads' variant='body2'>
    {text}
  </Note>
);

type Person = {
  title: string;
  /** Their answer to the opening question, as they might say it. */
  words: string;
  acknowledge: string;
  /** What we understood, said back as a sentence rather than a card. */
  repeat: string;
  /**
   * Once confirmed. Said what they need: what we could do, and whether it
   * helps. Didn't: a question to find out.
   */
  then: { says?: string[]; ask: string };
  /** The same, as 4.0's summary card. */
  heard: string[];
  home: ReactNode;
  why: string;
};

const PEOPLE: Person[] = [
  {
    title: 'Asked for money and care at home, started the claim',
    words:
      "It's my mum. Vascular dementia, and the arthritis is bad, so I'm round there most days, probably 40 hours a week. My sister helps when she can. I don't know if Mum should be getting any money, and I think we need carers coming in.",
    acknowledge:
      "Thank you for telling me. That's a lot to carry, even with your sister helping.",
    repeat:
      "So you care for your mum, around 40 hours a week. Your mum has vascular dementia and arthritis, and you'd like to know about money and getting carers in.",
    then: {
      says: [
        "From what you've told me, your mum might be able to get Attendance Allowance. That could be £76.70 to £114.60 a week. We can help you find carers nearby too.",
      ],
      ask: 'Would that help?',
    },
    heard: [
      "You're caring for your mum, about 40 hours a week, with help from your sister.",
      'Your mum is living with vascular dementia and walks with a frame. They need help washing, dressing and through the night.',
      "You'd like help with money your mum might be owed, and with care at home.",
    ],
    home: (
      <Stack spacing={3}>
        {lead(
          'Carry on',
          'Your Attendance Allowance claim is 3 of 8 answered. It could be £76.70 to £114.60 a week.',
          'Carry on with the claim',
        )}
        {toldUs([
          'You asked for help with money',
          'Needs help through the night',
          'You started the claim in onboarding',
        ])}
        {options('Also for you', ['Find carers near your mum', CALL])}
      </Stack>
    ),
    why: 'What they asked for, and already started. Care at home was asked for too, so it is first of the rest.',
  },
  {
    title: 'Coming home from hospital this week',
    words:
      "Dad had a fall and has been in hospital a week. They're saying Friday for coming home. Dad lives on his own and I'm two hours away. I just don't know how it's going to work.",
    acknowledge:
      "I'm sorry, that sounds really worrying, especially from two hours away.",
    repeat:
      "So your dad is in hospital after a fall and due home on Friday. Your dad lives alone, and you're not sure how they'll manage.",
    then: {
      says: [
        "From what you've told me, the most useful thing right now is a care expert to help you plan for Friday.",
      ],
      ask: 'Would that help?',
    },
    heard: [
      'Your dad is in hospital after a fall and is due home on Friday.',
      'Your dad lives alone, and you live two hours away.',
      "You're worried about how your dad will manage at home.",
    ],
    home: (
      <Stack spacing={3}>
        {lead(
          'Talk to a care expert today',
          'Your dad is due home on Friday. A care expert can help you plan for it, and tell you what to ask the hospital.',
          'Book a call',
        )}
        {toldUs([
          'Due home from hospital on Friday',
          'Lives alone',
          'You live two hours away',
        ])}
        {gap(
          'Help planning a hospital discharge',
          'Getting the right support in place before your dad comes home.',
        )}
        {options('Also for you', ['Find carers near your dad'])}
      </Stack>
    ),
    why: 'Urgency beats everything. What they need most is discharge help, which we do not offer, so the lead is the nearest thing we do.',
  },
  {
    title: "Didn't say what they need",
    words:
      "I look after my husband, who has Parkinson's. Getting around is harder now. We're both in our seventies, just the state pension, nothing else.",
    acknowledge:
      "Thank you for sharing that. It sounds like you're doing a lot for your husband.",
    repeat:
      "So you care for your husband, who has Parkinson's and finds getting around harder. You're both in your seventies, on the state pension.",
    // Nothing asked for, so the follow-up is a question, not an offer.
    then: {
      ask: 'What would help you most right now?',
    },
    heard: [
      "You're caring for your husband, who has Parkinson's and finds getting around harder.",
      "You're both over 66 and not getting any benefits.",
    ],
    home: (
      <Stack spacing={3}>
        {lead(
          'Things we can help with',
          "You're both over 66 and not getting any benefits. It takes about five minutes to see what you could be owed.",
          'Check eligibility',
        )}
        {toldUs([
          'No benefits yet',
          'Both 66 or over',
          'Needs help getting around',
        ])}
        {options('Also for you', [CALL])}
      </Stack>
    ),
    why: 'Nothing asked for and nothing urgent, so effort to value decides. Money they are probably missing, found out in minutes.',
  },
  {
    title: 'Worried about money as memory goes',
    words:
      "Mum's got early Alzheimer's. Still at home and mostly OK, but the memory's getting worse. I'm worried about the bills and who'll sort the money when Mum can't.",
    acknowledge:
      "That's a hard thing to be thinking about. Thank you for telling me.",
    repeat:
      "So your mum has early Alzheimer's and still lives at home. Your mum's memory is getting worse, and you're worried about managing the money and bills.",
    // What would help is a gap, so it is named and the call is offered.
    then: {
      says: [
        "From what you've told me, it might be worth setting up a Lasting Power of Attorney while your mum can still make decisions. We can't do that for you yet, but a care expert can talk you through it.",
      ],
      ask: 'Would that help?',
    },
    heard: [
      "You're helping your mum, who has early Alzheimer's and still lives at home.",
      "Your mum's memory is getting worse, and you're worried about who will manage their money and bills.",
    ],
    home: (
      <Stack spacing={3}>
        {lead(
          'Talk to a care expert',
          'A care expert can go through what to sort out while your mum can still make decisions.',
          'Book a call',
        )}
        {toldUs([
          'Memory getting worse',
          'Worried about money and bills',
          'Still lives at home',
        ])}
        {gap(
          'Setting up a Lasting Power of Attorney',
          "So you can manage your mum's money and decisions if they no longer can.",
        )}
        {options('Also for you', [
          'Check what you and your mum could be entitled to',
        ])}
      </Stack>
    ),
    why: 'What they asked for is a gap. The lead falls back to a call, where a care expert can point them on.',
  },
];

// What onboarding hears, the next step it points to, and whether we offer it.
const RULES: { heard: string; next: string; offered: boolean }[] = [
  {
    heard: 'Started something in onboarding',
    next: 'Carry on with it',
    offered: true,
  },
  {
    heard: 'Leaving hospital soon, a fall, a crisis',
    next: 'A call with a care expert',
    offered: true,
  },
  {
    heard: 'Leaving hospital soon',
    next: 'Help planning the discharge',
    offered: false,
  },
  {
    heard: 'Asks about money, or 66 or over with no benefits',
    next: 'Benefits calculator',
    offered: true,
  },
  { heard: 'Needs care at home', next: 'Find carers nearby', offered: true },
  {
    heard: 'Care needs never assessed',
    next: 'Care needs assessment',
    offered: true,
  },
  {
    heard: 'Paying for care, or about to',
    next: 'Financial assessment',
    offered: true,
  },
  {
    heard: 'Worried about decisions or money as memory goes',
    next: 'Lasting Power of Attorney',
    offered: false,
  },
  {
    heard: 'Nothing stands out',
    next: 'Calculator if money is likely, otherwise a call',
    offered: true,
  },
];

const rules = (
  <Note title='What onboarding hears, and what leads' variant='body2'>
    <Stack spacing={1.5}>
      <Typography variant='body2'>
        In order: what they asked for, urgency, what they are missing, effort to
        value, what it unlocks. Urgency beats everything.
      </Typography>
      <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
        {RULES.map((r) => (
          <Typography key={r.heard + r.next} component='li' variant='body2'>
            {r.heard}: {r.next}
            {r.offered ? '' : ' (not offered yet)'}
          </Typography>
        ))}
      </Box>
      <Typography variant='body2'>
        When the best next step is a gap, the lead falls back to a call.
      </Typography>
    </Stack>
  </Note>
);

/**
 * Home after onboarding, read from what was said in it. One row per person:
 * what they said, said back to them in the conversation, what comes next, then
 * the Home it gives them. Built on
 * `told-first` from Home, first visit: the lead, the answers behind it, then
 * quieter options. Needs we heard but can't meet show as gaps.
 */
export const homeNextSpec: HandoverSpec = {
  id: 'home-next',
  title: 'Home',
  description: '',
  canvas: true,
  links: [
    {
      label: 'Home, first visit',
      url: '/tell-us-once/flows/home-first-visit',
    },
  ],
  flows: [
    {
      title: 'What leads, and why',
      arrows: false,
      note: rules,
      steps: [],
    },
    ...PEOPLE.map((p, i) => ({
      title: p.title,
      numbered: false,
      steps: [
        {
          id: `home-next-${i + 1}-heard`,
          title: 'Said back to them',
          render: () => chat(repeatBack(p), 1),
          alt: {
            title: 'As 4.0 does it, the summary card',
            render: () => chat(summary(p.heard)),
          },
        },
        {
          id: `home-next-${i + 1}-then`,
          title: p.then.says ? 'What we could do' : 'What they need',
          render: () => chat(repeatBack(p, true), 2),
        },
        {
          id: `home-next-${i + 1}-home`,
          title: 'Home, first visit',
          render: () => homeTab(p.home),
          note: why(p.why),
        },
      ],
    })),
  ],
  states: [],
  components: {},
};
